import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    submitUrl: String,
    projectId: Number,
    submitting: String,
    submitBtnLabel: String,
    submitError: String
  }
  static targets = ["question", "submitBtn", "otherInput"]

  connect() {
    this._answers = new Map()
    this._hydrateFromDOM()
  }

  _hydrateFromDOM() {
    this.questionTargets.forEach(q => {
      const id = q.dataset.questionId
      const type = q.dataset.questionType
      if (type === "chip_multi") {
        const vals = [...q.querySelectorAll('.pw-iv__chip[aria-pressed="true"]:not(.pw-iv__chip--decide)')].map(c => c.dataset.value)
        const decided = q.querySelector('.pw-iv__chip--decide[aria-pressed="true"]')
        this._answers.set(id, { value: decided ? null : vals, decided_by_ai: !!decided })
      } else if (type === "chip_single") {
        const btn = q.querySelector('.pw-iv__chip[aria-pressed="true"]:not(.pw-iv__chip--decide)')
        const other = q.querySelector('.pw-iv__other-input')
        const decided = q.querySelector('.pw-iv__chip--decide[aria-pressed="true"]')
        let val = null
        if (decided) { val = null }
        else if (btn) { val = btn.dataset.value }
        else if (other && other.value) { val = other.value }
        this._answers.set(id, { value: val, decided_by_ai: !!decided })
      } else if (type === "textarea" || type === "url_input") {
        const input = q.querySelector('textarea, input[type="url"]')
        this._answers.set(id, { value: input ? input.value : null, decided_by_ai: false })
      } else if (type === "file_upload") {
        const decided = q.querySelector('.pw-iv__chip--decide[aria-pressed="true"]')
        this._answers.set(id, { value: null, decided_by_ai: !!decided })
      }
    })
  }

  toggleChip(event) {
    const btn = event.currentTarget
    const q = btn.closest('[data-interview-form-target="question"]')
    const type = q.dataset.questionType
    const id = q.dataset.questionId
    const value = btn.dataset.value

    // decide-for-me가 눌려있으면 해제
    const decideBtn = q.querySelector('.pw-iv__chip--decide')
    if (decideBtn) decideBtn.setAttribute('aria-pressed', 'false')

    if (type === "chip_single") {
      q.querySelectorAll('.pw-iv__chip:not(.pw-iv__chip--decide)').forEach(c => c.setAttribute('aria-pressed', 'false'))
      btn.setAttribute('aria-pressed', 'true')
      // other input clear
      const other = q.querySelector('.pw-iv__other-input')
      if (other) other.value = ''
      this._answers.set(id, { value, decided_by_ai: false })
    } else if (type === "chip_multi") {
      const now = btn.getAttribute('aria-pressed') === 'true'
      btn.setAttribute('aria-pressed', now ? 'false' : 'true')
      const vals = [...q.querySelectorAll('.pw-iv__chip[aria-pressed="true"]:not(.pw-iv__chip--decide)')].map(c => c.dataset.value)
      this._answers.set(id, { value: vals, decided_by_ai: false })
    }
  }

  toggleDecide(event) {
    const btn = event.currentTarget
    const q = btn.closest('[data-interview-form-target="question"]')
    const id = q.dataset.questionId
    const now = btn.getAttribute('aria-pressed') === 'true'
    const next = !now
    btn.setAttribute('aria-pressed', next ? 'true' : 'false')
    if (next) {
      // 다른 선택 해제
      q.querySelectorAll('.pw-iv__chip:not(.pw-iv__chip--decide)').forEach(c => c.setAttribute('aria-pressed', 'false'))
      const other = q.querySelector('.pw-iv__other-input')
      if (other) other.value = ''
      const ta = q.querySelector('textarea, input[type="url"]')
      if (ta) ta.value = ''
      this._answers.set(id, { value: null, decided_by_ai: true })
    } else {
      this._answers.set(id, { value: null, decided_by_ai: false })
    }
  }

  onOtherInput(event) {
    const input = event.currentTarget
    const q = input.closest('[data-interview-form-target="question"]')
    const type = q.dataset.questionType
    const id = q.dataset.questionId
    if (type === "chip_single" && input.value) {
      // 다른 chip 해제
      q.querySelectorAll('.pw-iv__chip').forEach(c => c.setAttribute('aria-pressed', 'false'))
      this._answers.set(id, { value: input.value, decided_by_ai: false })
    }
  }

  onTextarea(event) {
    const q = event.currentTarget.closest('[data-interview-form-target="question"]')
    this._answers.set(q.dataset.questionId, { value: event.currentTarget.value, decided_by_ai: false })
  }

  onUrl(event) {
    const q = event.currentTarget.closest('[data-interview-form-target="question"]')
    this._answers.set(q.dataset.questionId, { value: event.currentTarget.value, decided_by_ai: false })
  }

  onFileUpload(event) {
    // MVP: 파일 업로드는 Active Storage direct upload 붙이기 전까지 placeholder
    const q = event.currentTarget.closest('[data-interview-form-target="question"]')
    const id = q.dataset.questionId
    const file = event.currentTarget.files[0]
    this._answers.set(id, { value: file ? file.name : null, decided_by_ai: false })
    // TODO: 실제 업로드는 추후 구현
  }

  async submit(event) {
    event.preventDefault()
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.textContent = this.submittingValue || "저장 중..."
    }

    const answersObj = {}
    this._answers.forEach((v, k) => { answersObj[k] = v })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/html",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ answers: answersObj }),
        redirect: "follow",
        credentials: "same-origin",
      })
      if (res.redirected) {
        window.location.href = res.url
      } else if (res.ok) {
        window.location.reload()
      } else {
        throw new Error(`HTTP ${res.status}`)
      }
    } catch (e) {
      console.error("interview submit failed", e)
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = this.submitBtnLabelValue || "계속하기"
      }
      alert(this.submitErrorValue || "저장 중 오류가 발생했어요. 다시 시도해주세요.")
    }
  }
}
